import { keyCodes } from 'vuetify/lib/util/helpers'
const rippleStop = Symbol('rippleStop')
const DELAY_RIPPLE = 80

function isTouchEvent(e) {
  return e.constructor.name === 'TouchEvent'
}

function isKeyboardEvent(e) {
  return e.constructor.name === 'KeyboardEvent'
}

const ripples = {
  /* eslint-disable max-statements */
  show(e, el, value = {}) {
    if (!el._ripple || !el._ripple.enabled) {
      return
    }
    el.classList.add('e-button--is-active')
  },

  hide(el) {
    if (!el || !el._ripple || !el._ripple.enabled) return
    setTimeout(() => {
      el.classList.remove('e-button--is-active')
    }, 300)
  },
}

function isRippleEnabled(value) {
  return typeof value === 'undefined' || !!value
}

function rippleShow(e) {
  const value = {}
  const element = e.currentTarget
  if (!element || !element._ripple || element._ripple.touched || e[rippleStop]) return // Don't allow the event to trigger ripples on any other elements

  e[rippleStop] = true

  if (isTouchEvent(e)) {
    element._ripple.touched = true
    element._ripple.isTouch = true
  } else {
    // It's possible for touch events to fire
    // as mouse events on Android/iOS, this
    // will skip the event call if it has
    // already been registered as touch
    if (element._ripple.isTouch) return
  }

  value.center = element._ripple.centered || isKeyboardEvent(e)

  if (element._ripple.class) {
    value.class = element._ripple.class
  }

  if (isTouchEvent(e)) {
    // already queued that shows or hides the ripple
    if (element._ripple.showTimerCommit) return

    element._ripple.showTimerCommit = () => {
      ripples.show(e, element, value)
    }

    element._ripple.showTimer = window.setTimeout(() => {
      if (element && element._ripple && element._ripple.showTimerCommit) {
        element._ripple.showTimerCommit()

        element._ripple.showTimerCommit = null
      }
    }, DELAY_RIPPLE)
  } else {
    ripples.show(e, element, value)
  }
}

function rippleHide(e) {
  const element = e.currentTarget
  if (!element || !element._ripple) return
  window.clearTimeout(element._ripple.showTimer) // The touch interaction occurs before the show timer is triggered.
  // We still want to show ripple effect.

  if (e.type === 'touchend' && element._ripple.showTimerCommit) {
    element._ripple.showTimerCommit()

    element._ripple.showTimerCommit = null // re-queue ripple hiding

    element._ripple.showTimer = setTimeout(() => {
      rippleHide(e)
    })
    return
  }

  window.setTimeout(() => {
    if (element._ripple) {
      element._ripple.touched = false
    }
  })
  ripples.hide(element)
}

function rippleCancelShow(e) {
  const element = e.currentTarget
  if (!element || !element._ripple) return

  if (element._ripple.showTimerCommit) {
    element._ripple.showTimerCommit = null
  }

  window.clearTimeout(element._ripple.showTimer)
}

let keyboardRipple = false

function keyboardRippleShow(e) {
  if (!keyboardRipple && (e.keyCode === keyCodes.enter || e.keyCode === keyCodes.space)) {
    keyboardRipple = true
    rippleShow(e)
  }
}

function keyboardRippleHide(e) {
  keyboardRipple = false
  rippleHide(e)
}

function focusRippleHide(e) {
  if (keyboardRipple === true) {
    keyboardRipple = false
    rippleHide(e)
  }
}

function updateRipple(el, binding, wasEnabled) {
  const enabled = isRippleEnabled(binding.value)

  if (!enabled) {
    ripples.hide(el)
  }

  el._ripple = el._ripple || {}
  el._ripple.enabled = enabled
  const value = binding.value || {}

  if (value.center) {
    el._ripple.centered = true
  }

  if (value.class) {
    el._ripple.class = binding.value.class
  }

  if (value.circle) {
    el._ripple.circle = value.circle
  }

  if (enabled && !wasEnabled) {
    el.addEventListener('touchstart', rippleShow, {
      passive: true,
    })
    el.addEventListener('touchend', rippleHide, {
      passive: true,
    })
    el.addEventListener('touchmove', rippleCancelShow, {
      passive: true,
    })
    el.addEventListener('touchcancel', rippleHide)
    el.addEventListener('mousedown', rippleShow)
    el.addEventListener('mouseup', rippleHide)
    el.addEventListener('mouseleave', rippleHide)
    el.addEventListener('keydown', keyboardRippleShow)
    el.addEventListener('keyup', keyboardRippleHide)
    el.addEventListener('blur', focusRippleHide) // Anchor tags can be dragged, causes other hides to fail - #1537

    el.addEventListener('dragstart', rippleHide, {
      passive: true,
    })
  } else if (!enabled && wasEnabled) {
    removeListeners(el)
  }
}

function removeListeners(el) {
  el.removeEventListener('mousedown', rippleShow)
  el.removeEventListener('touchstart', rippleShow)
  el.removeEventListener('touchend', rippleHide)
  el.removeEventListener('touchmove', rippleCancelShow)
  el.removeEventListener('touchcancel', rippleHide)
  el.removeEventListener('mouseup', rippleHide)
  el.removeEventListener('mouseleave', rippleHide)
  el.removeEventListener('keydown', keyboardRippleShow)
  el.removeEventListener('keyup', keyboardRippleHide)
  el.removeEventListener('dragstart', rippleHide)
  el.removeEventListener('blur', focusRippleHide)
}

function directive(el, binding, node) {
  updateRipple(el, binding, false)

  if (process.env.NODE_ENV === 'development') {
    // warn if an inline element is used, waiting for el to be in the DOM first
    node.context &&
      node.context.$nextTick(() => {
        const computed = window.getComputedStyle(el)

        if (computed && computed.display === 'inline') {
          const context = node.fnOptions ? [node.fnOptions, node.context] : [node.componentInstance]
          console.log('v-ripple can only be used on block-level elements', ...context)
        }
      })
  }
}

function unbind(el) {
  delete el._ripple
  removeListeners(el)
}

function update(el, binding) {
  if (binding.value === binding.oldValue) {
    return
  }

  const wasEnabled = isRippleEnabled(binding.oldValue)
  updateRipple(el, binding, wasEnabled)
}

export const Ripple = {
  bind: directive,
  unbind,
  update,
}
export default Ripple
